import React, { useState, useEffect, useRef } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { Navigate } from 'react-router-dom';
import { Calendar, Loader2, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../context/LanguageContext';
import { useAuth } from '../../../context/AuthContext';
import { Meeting } from '../../../shared/types';
import { MultiStatusSelect } from '../../../components/ui/MultiStatusSelect';
import { StatusSelect } from '../../../components/ui/StatusSelect';
import { TransferStatusSelect } from '../../../components/ui/TransferStatusSelect';
import { DateTimePicker } from '../../../components/ui/DateTimePicker';
import { MeetingModal, Tab } from '../components/MeetingModal';
import { MoreOptionsMenu, MenuOption } from '../../../components/ui/MoreOptionsMenu';
import { ExportModal } from '../components/ExportModal';

// Define the structure for a group of meetings
interface MeetingsGroup {
  key: string; // Sortable key, e.g., "2023-07"
  meetings: Meeting[];
}

type MeetingsTab = 'meetings' | 'interested';

// Props for MeetingsPage
interface MeetingsPageProps {
  meetings: Meeting[];
  loadingInitial: boolean;
  loadingMoreMeetings: boolean;
  error: string | null;
  account: string | null;
  handleMeetingUpdate: (meetingId: string, fields: Partial<Record<string, unknown>>) => Promise<void>;
}

export const MeetingsPage = ({ meetings, loadingInitial, loadingMoreMeetings, error, account, handleMeetingUpdate }: MeetingsPageProps) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { user } = useAuth();
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [activeModalTab, setActiveModalTab] = useState<Tab>('contact');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [activeTab, setActiveTab] = useState<MeetingsTab>('meetings');

  const contextMenuRef = useRef<HTMLDivElement>(null);

  const menuOptions: MenuOption[] = [
    {
      key: 'export',
      label: t('export.downloadData'),
      icon: <Download className="w-4 h-4" />,
      onClick: () => setShowExportModal(true)
    }
  ];

  const handlePageWrapperClick = (e: React.MouseEvent) => {
    if (!selectedMeeting) return; // Modal not open
    if (contextMenuRef.current && contextMenuRef.current.contains(e.target as Node)) return;
    setSelectedMeeting(null);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
  };

  // Split meetings by tab. Pre-migration rows without meeting_type default
  // to 'meeting' (handled in fieldMapper) so they land on the Meetings tab.
  const interestedMeetings = meetings.filter(m => m.fields.meeting_type === 'interested_transfer');
  const realMeetings = meetings.filter(m => m.fields.meeting_type !== 'interested_transfer');
  const visibleMeetings = activeTab === 'interested' ? interestedMeetings : realMeetings;

  const groupedMeetings = visibleMeetings.reduce((groups: Record<string, MeetingsGroup>, meeting: Meeting) => {
    const fechaReunion = meeting.fields['Fecha Reunión'];
    if (!fechaReunion || typeof fechaReunion !== 'string') return groups;

    try {
      const date = parseISO(fechaReunion);
      if (!isValid(date)) return groups;

      const monthYearKey = format(date, 'yyyy-MM');
      const displayMonthYear = format(date, 'MMMM yyyy', { locale: language === 'es' ? es : enUS });

      if (!groups[displayMonthYear]) {
        groups[displayMonthYear] = { key: monthYearKey, meetings: [] };
      }
      groups[displayMonthYear].meetings.push(meeting);
      groups[displayMonthYear].meetings.sort((a: Meeting, b: Meeting) =>
        new Date(b.fields['Fecha Reunión']).getTime() - new Date(a.fields['Fecha Reunión']).getTime()
      );
    } catch (err) {
      console.error('Error parsing date or grouping meeting:', meeting.id, fechaReunion, err);
    }
    return groups;
  }, {});

  const handleCollapseAll = () => {
    const allGroups = Object.keys(groupedMeetings).reduce((acc, month) => {
      acc[month] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setCollapsedGroups(allGroups);
    setContextMenuPosition(null);
  };

  const handleExpandAll = () => {
    setCollapsedGroups({});
    setContextMenuPosition(null);
  };

  useEffect(() => {
    const handleClickOutside = () => setContextMenuPosition(null);
    if (contextMenuPosition) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenuPosition]);

  // Sync selectedMeeting with updated meetings data
  useEffect(() => {
    if (selectedMeeting) {
      const updatedMeeting = meetings.find(meeting => meeting.id === selectedMeeting.id);
      if (updatedMeeting && updatedMeeting !== selectedMeeting) {
        setSelectedMeeting(updatedMeeting);
      }
    }
  }, [meetings, selectedMeeting]);

  // Auto-switch back to Meetings when the Interested list empties (e.g., the
  // operator promoted the last row). Avoids being stranded on a hidden tab.
  useEffect(() => {
    if (activeTab === 'interested' && interestedMeetings.length === 0) {
      setActiveTab('meetings');
    }
  }, [activeTab, interestedMeetings.length]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isInterestedTab = activeTab === 'interested';

  return (
    <div className="h-full overflow-y-auto [scrollbar-gutter:stable]" onClick={handlePageWrapperClick}>
      <main className="max-w-7xl mx-auto py-6">
        <div className="mb-6 px-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center space-x-2">
            <h1 className="text-lg font-medium text-gray-900">{t('meetings.title')}</h1>
            <span className="text-lg text-gray-500">·</span>
            <span className="text-lg text-gray-500">{account}</span>
          </div>
          <div className="flex items-center gap-2">
            <MultiStatusSelect
              selectedStatuses={selectedStatuses}
              onChange={setSelectedStatuses}
            />
            <MoreOptionsMenu options={menuOptions} />
          </div>
        </div>

        {/* Tabs: Meetings (real bookings + direct calls) vs Interested (transfers).
            Hidden entirely when the account has no Interested transfers — the
            page falls back to its pre-feature single-list layout. */}
        {interestedMeetings.length > 0 && (
          <div className="px-8 mb-4 border-b border-gray-200">
            <nav className="flex gap-6 -mb-px" aria-label="Tabs">
              <button
                type="button"
                onClick={() => setActiveTab('meetings')}
                className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'meetings'
                    ? 'border-[#0055FF] text-[#0055FF]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('meetings.tabMeetings')}
                <span className="ml-1.5 text-xs text-gray-400">({realMeetings.length})</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('interested')}
                className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'interested'
                    ? 'border-[#0055FF] text-[#0055FF]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('meetings.tabInterested')}
                <span className="ml-1.5 text-xs text-gray-400">({interestedMeetings.length})</span>
              </button>
            </nav>
          </div>
        )}

        {loadingInitial && meetings.length === 0 ? (
          <div className="flex items-center justify-center py-12 px-8">
            <Loader2 className="w-5 h-5 animate-spin text-[#0055FF]" />
            <span className="ml-2 text-sm text-gray-500">
              {isInterestedTab ? t('meetings.loadingInterested') : t('meetings.loadingMeetings')}
            </span>
          </div>
        ) : error ? (
          <div className="text-center py-12 px-8">
            <p className="text-sm text-red-600 font-medium mb-2">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-sm text-[#0055FF] hover:text-[#0044CC]"
            >
              {t('meetings.retryAgain')}
            </button>
          </div>
        ) : Object.keys(groupedMeetings).length === 0 && !loadingInitial ? (
          <div className="text-center py-12 px-8">
            <p className="text-sm text-gray-500">
              {isInterestedTab ? t('meetings.noInterestedTransfers') : t('meetings.noMeetingsScheduled')}
            </p>
          </div>
        ) : (
          <div className="space-y-6 px-8">
            {Object.entries(groupedMeetings)
              .sort(([, groupA], [, groupB]) => groupB.key.localeCompare(groupA.key))
              .map(([displayMonth, group]) => {
                const filteredMeetings = group.meetings.filter((meeting: Meeting) => {
                  if (selectedStatuses.length === 0) return true;
                  const status = meeting.fields['Status Lead'];
                  return typeof status === 'string' && selectedStatuses.includes(status);
                });

                if (filteredMeetings.length === 0) return null;

                return (
                  <div key={displayMonth} className="bg-white shadow-sm border border-gray-200">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCollapsedGroups(prev => ({ ...prev, [displayMonth]: !prev[displayMonth] }));
                      }}
                      onContextMenu={handleContextMenu}
                      className="w-full px-4 py-3 border-b border-gray-200 hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-2.5 text-left">
                        <svg
                          className={`w-3 h-3 text-gray-400 transform transition-transform ${
                            collapsedGroups[displayMonth] ? '-rotate-90' : ''
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        <Calendar className="w-4 h-4 text-[#0055FF]" />
                        <h2 className="text-sm font-medium text-gray-900">{displayMonth}</h2>
                        <span className="text-sm text-gray-500">·</span>
                        <span className="text-sm text-gray-500">{filteredMeetings.length}</span>
                      </div>
                    </button>
                    <div className={`overflow-x-auto ${collapsedGroups[displayMonth] ? 'hidden' : ''}`}>
                      <table className="w-full table-fixed">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="w-[14%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('meetings.name')}</th>
                            <th className="w-[10%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('meetings.company')}</th>
                            <th className="w-[4%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('meetings.type')}</th>
                            <th className="w-[10%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {isInterestedTab ? t('meetings.transferredDate') : t('meetings.meetingDate')}
                            </th>
                            <th className="w-[14%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('meetings.leadStatus')}</th>
                            <th className="w-[12%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('meetings.notes')}</th>
                            <th className="w-[13%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('meetings.contactAgain')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {filteredMeetings.map((meeting: Meeting) => {
                            const isTransferRow = meeting.fields.meeting_type === 'interested_transfer';
                            return (
                              <tr
                                key={meeting.id}
                                className={`hover:bg-gray-50 cursor-pointer text-sm h-12 ${
                                  selectedMeeting?.id === meeting.id ? 'bg-gray-50' : ''
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedMeeting(meeting);
                                }}
                              >
                                <td className="px-4 py-2.5 font-medium text-gray-900 overflow-hidden">
                                  <div className="truncate">{meeting.fields.Nombre}</div>
                                </td>
                                <td className="px-4 py-2.5 text-gray-600 overflow-hidden">
                                  <div className="truncate">{meeting.fields.Empresa}</div>
                                </td>
                                <td className="px-4 py-2.5">
                                  {isTransferRow ? (
                                    <span
                                      className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-teal-100 text-teal-800"
                                      title={t('meetings.interestedTooltip')}
                                    >
                                      {t('meetings.interestedBadge')}
                                    </span>
                                  ) : meeting.fields.meeting_type === 'transferred_meeting' ? (
                                    <span
                                      className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-teal-100 text-teal-800"
                                      title={t('meetings.transferredTooltip')}
                                    >
                                      {t('meetings.transferredBadge')}
                                    </span>
                                  ) : (
                                    <span
                                      className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                        meeting.fields.is_direct_call
                                          ? 'bg-orange-100 text-orange-800'
                                          : 'bg-blue-100 text-blue-800'
                                      }`}
                                      title={meeting.fields.is_direct_call ? t('meetings.directCall') : t('meetings.meetingType')}
                                    >
                                      {meeting.fields.is_direct_call ? 'LD' : 'R'}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2.5 text-gray-900 whitespace-nowrap">
                                  <div onClick={(e) => e.stopPropagation()}>
                                    <DateTimePicker
                                      value={meeting.fields['Fecha Reunión'] || ''}
                                      onChange={async (date) => {
                                        await handleMeetingUpdate(meeting.id, { 'Fecha Reunión': date });
                                      }}
                                      allowClear={false}
                                    />
                                  </div>
                                </td>
                                <td className="px-4 py-2.5 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                                  <div className="w-full">
                                    {isTransferRow ? (
                                      <TransferStatusSelect
                                        value={meeting.fields.transfer_status ?? null}
                                        onChange={(status) => handleMeetingUpdate(meeting.id, { transfer_status: status })}
                                      />
                                    ) : (
                                      <StatusSelect
                                        value={meeting.fields['Status Lead'] || null}
                                        onChange={(status) => handleMeetingUpdate(meeting.id, { 'Status Lead': status })}
                                      />
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-2.5 overflow-hidden">
                                  <div
                                    className="text-sm text-gray-600 cursor-pointer hover:text-gray-900 truncate"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedMeeting(meeting);
                                      setActiveModalTab('notes');
                                    }}
                                  >
                                    {meeting.fields.Notas || ''}
                                  </div>
                                </td>
                                <td className="px-4 py-2.5 text-gray-900 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                  <div onClick={(e) => e.stopPropagation()}>
                                    <DateTimePicker
                                      value={meeting.fields['Volver a contactar'] || ''}
                                      onChange={async (date) => {
                                        await handleMeetingUpdate(meeting.id, { 'Volver a contactar': date });
                                      }}
                                      allowClear={true}
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            {meetings.length > 0 && loadingMoreMeetings && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-[#0055FF]" />
                <span className="ml-2 text-sm text-gray-500">{t('meetings.loadingMoreMeetings')}</span>
              </div>
            )}
          </div>
        )}

        {contextMenuPosition && (
          <div
            ref={contextMenuRef}
            className="fixed bg-white shadow-lg border border-gray-200 py-1 z-50"
            style={{
              top: `${contextMenuPosition.y}px`,
              left: `${contextMenuPosition.x}px`,
            }}
          >
            <button
              onClick={handleCollapseAll}
              className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
            >
              {t('meetings.collapseAll')}
            </button>
            <button
              onClick={handleExpandAll}
              className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
            >
              {t('meetings.expandAll')}
            </button>
          </div>
        )}

        {selectedMeeting && (
          <MeetingModal
            meeting={selectedMeeting}
            onClose={() => setSelectedMeeting(null)}
            onStatusUpdate={async (status) => {
              await handleMeetingUpdate(selectedMeeting.id, { 'Status Lead': status });
            }}
            onDateUpdate={async (date) => {
              await handleMeetingUpdate(selectedMeeting.id, { 'Fecha Reunión': date });
            }}
            onContactUpdate={async (fieldsToUpdate) => {
              await handleMeetingUpdate(selectedMeeting.id, fieldsToUpdate);
            }}
            activeTab={activeModalTab}
            setActiveTab={setActiveModalTab}
            onNotesUpdate={async (notes) => {
              await handleMeetingUpdate(selectedMeeting.id, {
                Notas: notes,
                notes_updated_at: new Date().toISOString(),
                notes_updated_by: user?.email ?? null,
              });
            }}
            isOpen={!!selectedMeeting}
          />
        )}

        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          accountName={account || ''}
        />
      </main>
    </div>
  );
};
